import { useState } from 'react';
import { X, HelpCircle } from 'lucide-react';

export function ModuleGroupingHelpButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-6 h-6 ml-2 text-blue-600 rounded-full bg-blue-50 border border-blue-200 hover:bg-blue-600 hover:text-white transition-colors"
        title="Module Grouping Help"
      >
        <span className="text-sm font-bold">?</span>
      </button>
      {open && <ModuleGroupingHelpModal onClose={() => setOpen(false)} />}
    </>
  );
}

interface ModuleGroupingHelpModalProps {
  onClose: () => void;
}

export function ModuleGroupingHelpModal({ onClose }: ModuleGroupingHelpModalProps) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white p-6 flex items-center justify-between border-b">
          <h2 className="text-2xl font-bold">Module Grouping: How It Works</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-emerald-700 rounded transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              What is Module Grouping?
            </h3>
            <p className="text-gray-700 mb-3">
              Module Grouping allows you to <strong>GROUP</strong> related modules together under a canonical (primary) module name. It enables:
            </p>
            <ul className="space-y-2 text-gray-700 ml-4">
              <li>✓ <strong>Module aliasing:</strong> Map multiple module names to one canonical module</li>
              <li>✓ <strong>Automatic expansion:</strong> When any group member is detected, all members are included</li>
              <li>✓ <strong>Cross-module queries:</strong> Search across grouped tables with a single module lookup</li>
              <li>✓ <strong>Simplified configuration:</strong> Define relationships once, use everywhere</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Real-World Example: Revenue Modules
            </h3>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Revenue Module Group</h4>
              <div className="space-y-3 text-sm text-gray-800">
                <p><strong>Canonical Module:</strong> "Revenue Processing"</p>
                <div className="bg-white border border-emerald-200 rounded p-3">
                  <p className="font-medium mb-2">Members:</p>
                  <ul className="ml-4 space-y-1 text-gray-700">
                    <li>• Revenue Report</li>
                    <li>• Revenue Recognition</li>
                    <li>• Weekly Revenue Report</li>
                    <li>• Monthly Revenue Summary</li>
                  </ul>
                </div>
                <p className="text-emerald-700 font-medium">
                  Result: When ticket mentions ANY of these → System detects "Revenue Processing" module
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Complete Flow: Module Grouping in Action
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <p className="font-semibold text-gray-900">Create Module Grouping</p>
                  <p className="text-gray-600">Define canonical module + add members</p>
                </div>
              </div>
              <div className="text-center text-emerald-600 text-lg">↓</div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <p className="font-semibold text-gray-900">System Detects Group Member</p>
                  <p className="text-gray-600">Ticket mentions "Weekly Revenue Report" (a group member)</p>
                </div>
              </div>
              <div className="text-center text-emerald-600 text-lg">↓</div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <p className="font-semibold text-gray-900">Module Expansion Resolves</p>
                  <p className="text-gray-600">System looks up grouping: "Weekly Revenue Report" → Canonical "Revenue Processing"</p>
                </div>
              </div>
              <div className="text-center text-emerald-600 text-lg">↓</div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <p className="font-semibold text-gray-900">Canonical Module Used for Table Lookup</p>
                  <p className="text-gray-600">Neo4j queries with canonical name "Revenue Processing" → Returns all related tables</p>
                </div>
              </div>
              <div className="text-center text-emerald-600 text-lg">↓</div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">5</div>
                <div>
                  <p className="font-semibold text-gray-900">Cross-Module Tables Queried</p>
                  <p className="text-gray-600">System finds tables for all group members at once</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Module Grouping Fields & Their Purpose
            </h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Canonical Module Name</h4>
                <p className="text-sm text-gray-700">
                  <strong>What:</strong> The primary/official name for this group<br />
                  <strong>Purpose:</strong> Used for Neo4j table lookups<br />
                  <strong>Rules:</strong> Must match exact module name in Neo4j<br />
                  <strong>Example:</strong> "Revenue Processing" (must exist as a module)
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">⚠️ Group Members (CRITICAL)</h4>
                <p className="text-sm text-gray-700">
                  <strong>What:</strong> Aliases or related module names<br />
                  <strong>Purpose:</strong> When LLM detects ANY of these, canonical module is used<br />
                  <strong>Format:</strong> One per line, exact names<br />
                  <strong>Example:</strong> Revenue Report, Weekly Revenue Report, Monthly Revenue Summary
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Description</h4>
                <p className="text-sm text-gray-700">
                  <strong>What:</strong> Why these modules are grouped together<br />
                  <strong>Purpose:</strong> Analyst documentation<br />
                  <strong>Example:</strong> "All revenue-related reporting and processing modules grouped for unified table discovery"
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Critical Constraints (Must Avoid)
            </h3>
            <div className="space-y-4">
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold text-red-900 text-sm mb-2">❌ Canonical Module Doesn't Exist in Neo4j</h4>
                <p className="text-sm text-red-800">
                  <strong>Problem:</strong> Canonical name "Revenue Processing" doesn't exist as a module in Neo4j<br />
                  <strong>Result:</strong> Module expansion fails → No tables found → Analysis fails<br />
                  <strong>Fix:</strong> Verify canonical name exists in Neo4j before creating grouping
                </p>
              </div>

              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold text-red-900 text-sm mb-2">❌ Member Not Recognized by LLM</h4>
                <p className="text-sm text-red-800">
                  <strong>Problem:</strong> Group member name is too vague or doesn't match ticket text<br />
                  <strong>Result:</strong> LLM detects different module, grouping never triggered<br />
                  <strong>Fix:</strong> Use exact names that LLM will recognize + add common variations
                </p>
              </div>

              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold text-red-900 text-sm mb-2">❌ Circular or Cross-Group Dependencies</h4>
                <p className="text-sm text-red-800">
                  <strong>Problem:</strong> Module A is in group 1, also in group 2 OR group A and B both map to C<br />
                  <strong>Result:</strong> Module expansion produces duplicates or ambiguous results<br />
                  <strong>Fix:</strong> Each module can be in ONLY ONE group. Canonical names must be unique.
                </p>
              </div>

              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <h4 className="font-semibold text-blue-900 text-sm mb-2">ℹ️ Constraint: One Module, One Group</h4>
                <p className="text-sm text-blue-800">
                  A module can belong to only ONE grouping. If you need a module in multiple groups, reconsider the grouping strategy or create more granular groupings.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Best Practices
            </h3>
            <div className="space-y-4">
              <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50">
                <h4 className="font-semibold text-emerald-900 text-sm mb-2">✓ Good Grouping Strategy</h4>
                <div className="text-sm text-emerald-900 space-y-2">
                  <p><strong>Group Name:</strong> "Revenue Processing"</p>
                  <p><strong>Members:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Revenue Report</li>
                    <li>• Revenue Recognition</li>
                    <li>• Weekly Revenue Report</li>
                    <li>• Monthly Revenue Summary</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-emerald-200">
                    <p className="text-xs">✓ Canonical name exists in Neo4j</p>
                    <p className="text-xs">✓ Members are specific module names</p>
                    <p className="text-xs">✓ Clear semantic relationship (all revenue)</p>
                    <p className="text-xs">✓ No overlaps with other groups</p>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">🔑 Key Principles</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>Canonical first:</strong> Choose canonical name that exists in Neo4j</li>
                  <li><strong>Be specific:</strong> Members should be exact module names LLM recognizes</li>
                  <li><strong>No cycles:</strong> Groupings cannot be circular or duplicate members</li>
                  <li><strong>One home:</strong> Each module belongs to exactly one grouping</li>
                  <li><strong>Semantic cohesion:</strong> Group modules that represent the same business concept</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-gradient-to-b from-emerald-600 to-emerald-800 rounded"></span>
              Integration with Entity Types
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="text-sm text-gray-800">
                <p className="mb-3"><strong>Both systems must succeed for data retrieval to work:</strong></p>
                <div className="ml-4 space-y-2">
                  <p><strong>1. Module Grouping (resolves module)</strong></p>
                  <p className="text-gray-600 ml-4">↓</p>
                  <p><strong>2. Entity Type (resolves columns)</strong></p>
                  <p className="text-gray-600 ml-4">↓</p>
                  <p><strong>3. WHERE clause generation</strong></p>
                </div>
              </div>
              <div className="border-t border-blue-200 pt-3 text-sm text-gray-700 space-y-1">
                <p><strong>If Module Grouping fails:</strong> No tables found → Analysis fails</p>
                <p><strong>If Entity Type fails:</strong> Tables found but columns not matched</p>
                <p><strong>If Both succeed:</strong> Correct SQL generated and executed ✅</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-8 py-4 flex justify-between items-center text-xs text-gray-500">
          <span>Module Grouping Help Guide</span>
          <span>Version 1.0</span>
        </div>
      </div>
    </div>
  );
}
